import { memo } from "react";
import { useIntl, FormattedMessage } from "react-intl";

import { useForm } from "common/core/form";
import { defaultRequiredMessage, FormattedFieldError } from "common/core/form/error";
import { RadioLabel, RadioGroup, RadioInput } from "common/core/form/option";

import { SELF_WITNESS_RELATIONSHIP } from "./notary_self_witness";
import { Actions, Fields, Form } from "./form";
import type {
  NotaryAddWitness_documentBundle as DocumentBundle,
  NotaryAddWitness_meetingParticipants as MeetingParticipant,
} from "./index_fragment.graphql";

type WitnessKind = "notary-self-witness" | "fully-remote" | "notary-colocated" | "signer-colocated";
export type FormValues = {
  kind: WitnessKind;
};
type Props = {
  onSubmit: (formValues: FormValues) => void;
  meetingParticipants: MeetingParticipant[];
  documentBundle: DocumentBundle;
  notaryUserId: string;
};

function hasNotarySelfWitnessParticipant(
  notaryUserId: string,
  meetingParticipants: MeetingParticipant[],
): boolean {
  const { id } = meetingParticipants.find((part) => part.userId === notaryUserId)!;
  return Boolean(
    meetingParticipants.find(
      (part) =>
        part.__typename === "WitnessParticipant" &&
        part.parentId === id &&
        part.relationshipToParent === SELF_WITNESS_RELATIONSHIP,
    ),
  );
}

function ChooseWitnessKind({ onSubmit, documentBundle, notaryUserId, meetingParticipants }: Props) {
  const form = useForm<FormValues>();
  const intl = useIntl();
  // Right now, only REAL transactions with the state of Florida are allowed.
  const orgTransactionPropertyState =
    documentBundle.organizationTransaction?.streetAddress?.state.toLowerCase();
  const isRemoteWitnessingPossible =
    orgTransactionPropertyState === "florida" || orgTransactionPropertyState === "fl";
  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <Fields>
        <RadioGroup
          label={
            <FormattedMessage
              id="5df54ff6-a567-4193-864a-755b865d0bbe"
              defaultMessage="Where is the witness located?"
            />
          }
          groupError={<FormattedFieldError inputName="kind" error={form.formState.errors.kind} />}
        >
          {isRemoteWitnessingPossible && (
            <RadioLabel
              label={
                <FormattedMessage
                  id="bd19e196-2d0a-4b57-8bae-38ec62db3927"
                  defaultMessage="I am the witness"
                />
              }
              radio={
                <RadioInput<WitnessKind>
                  value="notary-self-witness"
                  disabled={hasNotarySelfWitnessParticipant(notaryUserId, meetingParticipants)}
                  {...form.register("kind", { required: defaultRequiredMessage(intl) })}
                />
              }
            />
          )}

          {isRemoteWitnessingPossible && (
            <RadioLabel
              label={
                <FormattedMessage
                  id="ca312d9f-038b-4460-87cf-cfce3c8b23e5"
                  defaultMessage="The witness is physically with me"
                />
              }
              radio={
                <RadioInput<WitnessKind>
                  value="notary-colocated"
                  {...form.register("kind", { required: defaultRequiredMessage(intl) })}
                />
              }
            />
          )}

          <RadioLabel
            label={
              <FormattedMessage
                id="84cefb34-f936-4bfb-82cb-cdb7d095d791"
                defaultMessage="The witness is physically with the signer"
              />
            }
            radio={
              <RadioInput<WitnessKind>
                value="signer-colocated"
                {...form.register("kind", { required: defaultRequiredMessage(intl) })}
              />
            }
          />

          {isRemoteWitnessingPossible && (
            <RadioLabel
              label={
                <FormattedMessage
                  id="8615cd77-d473-4544-a31d-b0973af238be"
                  defaultMessage="The witness is fully remote (neither with me, nor a signer)"
                />
              }
              radio={
                <RadioInput<WitnessKind>
                  value="fully-remote"
                  {...form.register("kind", { required: defaultRequiredMessage(intl) })}
                />
              }
            />
          )}
        </RadioGroup>
      </Fields>

      <Actions>
        <FormattedMessage id="c8e04b13-79d6-4b30-8758-e3ec16394cf7" defaultMessage="Continue" />
      </Actions>
    </Form>
  );
}

export default memo(ChooseWitnessKind);
